import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';

/**
 * 스마트 네비게이션 훅 - 히스토리 관리 기능을 포함한 네비게이션
 * 
 * 기능:
 * 1. 동일 경로 중복 방지 (replace 사용)
 * 2. 방문 기록 추적
 * 3. 특수 페이지(settings 등) 최적화
 * 4. 뒤로가기 개선
 */
export const useSmartNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastBackPressRef = useRef(0);
  
  // 메인 페이지 경로 동적 구성
  const mainPagePath = useCallback(() => {
    const pathParts = location.pathname.split('/');
    const id = pathParts[2]; // /ws/{id}/...
    return `/ws/${id}/mobile/main`;
  }, [location.pathname]);
  
  // 방문 기록 초기화 (첫 렌더링 시)
  useEffect(() => {
    // 이미 방문 기록이 있으면 초기화하지 않음
    if (!sessionStorage.getItem('visitedPaths')) {
      sessionStorage.setItem('visitedPaths', JSON.stringify([location.pathname]));
    }
  }, []);
  
  // 방문 기록 가져오기
  const getVisitedPaths = useCallback(() => {
    const paths = sessionStorage.getItem('visitedPaths');
    return paths ? JSON.parse(paths) : [];
  }, []);
  
  // 방문 기록에 경로 추가
  const addToVisitedPaths = useCallback((path) => {
    const visitedPaths = getVisitedPaths();
    // 마지막 방문 경로와 같으면 추가하지 않음
    if (visitedPaths.length > 0 && visitedPaths[visitedPaths.length - 1] === path) {
      return;
    }
    visitedPaths.push(path);
    sessionStorage.setItem('visitedPaths', JSON.stringify(visitedPaths));
  }, [getVisitedPaths]);
  
  // 특수 페이지 목록 (replace로 처리할 페이지)
  const specialPages = ['/settings', '/preferences', '/modal'];
  
  // 뒤로가기 2번 탭 감지 및 처리
  const handleBackPress = useCallback(() => {
    const currentTime = new Date().getTime();
    const timeDiff = currentTime - lastBackPressRef.current;
    
    if (timeDiff < 500) { // 500ms 이내에 2번 탭
      if (window.BRIDGE && typeof window.BRIDGE.closeWebView === "function") {
        window.BRIDGE.closeWebView();
      } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.BRIDGE) {
        window.webkit.messageHandlers.BRIDGE.postMessage('closeWebView');
      }
    } else {
      lastBackPressRef.current = currentTime;
    }
  }, []);
  
  // 스마트 네비게이션
  const smartNavigate = useCallback((to, options = {}) => {
    // 메인 페이지로 이동 시 히스토리 스택 초기화
    if (to.includes(mainPagePath())) {
      options.replace = true;
    }
    
    navigate(to, options);
  }, [navigate, mainPagePath]);
  
  // 스마트 뒤로가기
  const goBack = useCallback(() => {
    const currentPath = location.pathname;
    
    // settings 관련 페이지 처리
    if (currentPath.includes('/settings')) {
      // settings의 하위 페이지인 경우
      if (currentPath.split('/').length > 4) { // /ws/{id}/mobile/settings/...
        // 상위 settings 페이지로 이동
        const parentPath = currentPath.split('/').slice(0, -1).join('/');
        navigate(parentPath, { replace: true });
        return;
      }
      
      // 최상위 settings 페이지인 경우 메인 페이지로 이동
      const mainPath = mainPagePath();
      window.location.href = mainPath; // WebView의 URL을 직접 변경
      return;
    }
    
    // 메인 페이지로 돌아가기
    if (!currentPath.includes(mainPagePath())) {
      const mainPath = mainPagePath();
      window.location.href = mainPath; // WebView의 URL을 직접 변경
      return;
    }
    
    // 기본 뒤로가기
    navigate(-1);
  }, [navigate, location.pathname, mainPagePath]);
  
  return {
    navigate: smartNavigate,
    goBack,
    getVisitedPaths
  };
};

export default useSmartNavigate; 